/**
 * Source of truth for the user's light/dark theme choice.
 *
 * Three preferences are stored: "light", "dark", or "system" (follow the OS).
 * The preference is persisted to localStorage (durable across refreshes, shared
 * across tabs) and resolved to a concrete "light" | "dark" that is reflected
 * onto <html> as the `.dark` class — the same class Tailwind's `darkMode:
 * "class"` and the token overrides in tokens.css key off.
 *
 * IMPORTANT: `STORAGE_KEY` and the resolution rule are duplicated by the tiny
 * pre-paint script in index.html (it runs before this module loads to set the
 * class with no flash). Keep the two in sync — key name, the light/dark/system
 * values, and "system → matchMedia".
 */
export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "composo.theme";
const PREFERENCES: readonly ThemePreference[] = ["light", "dark", "system"];

export function isThemePreference(value: unknown): value is ThemePreference {
  return (
    typeof value === "string" &&
    (PREFERENCES as readonly string[]).includes(value)
  );
}

/** The persisted preference, or "system" when unset/invalid/unavailable. */
export function readPersistedPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isThemePreference(raw) ? raw : "system";
  } catch {
    // localStorage can be unavailable (private mode, disabled). Degrade to the
    // system default for the life of the page rather than crashing.
    return "system";
  }
}

export function persistPreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Persistence is best-effort; the in-memory value still themes this page.
  }
}

/** Whether the OS currently asks for a dark UI. */
export function systemPrefersDark(): boolean {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

/**
 * The whole resolution rule, pure and in one place so it can be unit-tested:
 * an explicit light/dark wins; "system" defers to the OS preference.
 */
export function resolveTheme(
  preference: ThemePreference,
  prefersDark: boolean
): ResolvedTheme {
  if (preference === "system") return prefersDark ? "dark" : "light";
  return preference;
}

/** Browser-chrome colour (mobile address bar) matching each surface. */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: "#f7f9fb",
  dark: "#111722",
};

/**
 * Reflect the resolved theme onto the document: toggle `.dark` (drives every
 * token + `dark:` variant) and keep `color-scheme` and the theme-color meta in
 * step so native controls and the mobile browser chrome match.
 */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[resolved]);
}

/**
 * Mirror a preference change made in ANOTHER tab (the `storage` event fires only
 * in tabs that did not make the change). Returns an unsubscribe function.
 */
export function subscribeToPreferenceChanges(
  onChange: (preference: ThemePreference) => void
): () => void {
  const handler = (event: StorageEvent) => {
    // key === null means localStorage was cleared wholesale; otherwise ignore
    // keys that aren't ours.
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    onChange(isThemePreference(event.newValue) ? event.newValue : "system");
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}

/**
 * Fire `onChange` when the OS light/dark preference flips — so a "System" choice
 * updates live (e.g. an OS sunset auto-switch) without a reload. Returns an
 * unsubscribe function.
 */
export function subscribeToSystemChanges(onChange: () => void): () => void {
  try {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  } catch {
    return () => {};
  }
}
